import React, { Fragment, useState, useEffect, Component } from 'react';
import PropTypes from 'prop-types';
import './countdown-timer.style.scss';
import classNames from 'classnames';
import { formatDate } from '@thd-olt-functional/utils';
import helpers from '../helpers/countdownHelper';
import TimerInline from './partials/TimerInline';
import TimerBlock from './partials/TimerBlock';

const CountdownTimer = ({
  endDate,
  timebombThreshold,
  daysLeftThreshold,
  dateDisplayThreshold,
  hours,
  minutes,
  shouldShowSecondsRemaining,
  shouldUseMessagingString,
  shouldIncludeSvg,
  shouldUseBlock,
  shouldUseClockDisplay,
  shouldUseDateDisplay,
  shouldUseDaysLeftDisplay,
  customMessagingString,
  isSBOTD
}) => {

  const initializeEndDate = () => {
    if (endDate) {
      return new Date(endDate);
    }
    if (hours || minutes) {
      const tempDate = new Date();
      if (hours) {
        tempDate.setHours(tempDate.getHours() + +hours);
      }
      if (minutes) {
        tempDate.setMinutes(tempDate.getMinutes() + +minutes);
      }
      tempDate.setSeconds(59);
      return tempDate;
    }
    return 0;
  };

  const [timerEndDate, setTimerEndDate] = useState(initializeEndDate());

  const calculateTimeLeft = () => {
    if (!timerEndDate) {
      return 0;
    }
    let newTimeLeft = null;
    if (timerEndDate && isSBOTD) {
      newTimeLeft = helpers.calculateSbotdTimeLeft();
    }
    if (!isSBOTD) {
      const timeToExpiration = +new Date(timerEndDate) - +new Date();

      if (timeToExpiration > 0) {
        newTimeLeft = {
          days: Math.floor(timeToExpiration / (1000 * 60 * 60 * 24)),
          timerHours: Math.floor((timeToExpiration / (1000 * 60 * 60)) % 24),
          timerMinutes: Math.floor((timeToExpiration / 1000 / 60) % 60),
          seconds: Math.floor((timeToExpiration / 1000) % 60),
          totalMilliseconds: timeToExpiration
        };
      }
    }

    return newTimeLeft;
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  const getTimeStringDate = () => {

    return formatDate({ date: timerEndDate, shortMonth: true, dayMonthDateFormat: true });
  };

  const getTimeStringDaysHoursMinutes = () => {

    const daySuffix = (timeLeft || {}).days === 1 ? ' day' : ' days';
    const hourSuffix = (timeLeft || {}).timerHours === 1 ? ' hr' : ' hours';
    const minuteSuffix = (timeLeft || {}).timerMinutes === 1 ? ' min' : ' mins';
    const secondSuffix = (timeLeft || {}).seconds === 1 ? ' sec' : ' secs';

    const days = timeLeft?.days ? `${timeLeft.days}${(daySuffix)}` : '';
    const timerHours = timeLeft?.timerHours ? ` ${timeLeft.timerHours}${(hourSuffix)}` : '';
    const timerMinutes = timeLeft?.timerMinutes ? ` ${timeLeft.timerMinutes}${(minuteSuffix)}` : '';
    const timerSeconds = shouldShowSecondsRemaining && timeLeft?.seconds ? ` ${timeLeft.seconds}${(secondSuffix)}` : '';
    return `${days}${timerHours}${timerMinutes}${timerSeconds}`;
  };

  const getTimeStringDigitalClock = () => {

    let newTimeString = '';

    const { days, timerHours, timerMinutes, seconds } = timeLeft;
    /* eslint-disable max-len */
    newTimeString += `${((days * 24 + timerHours) >= 10 ? days * 24 + timerHours : '0'.concat(timerHours))}`;
    newTimeString += `:${(timerMinutes >= 10 ? timerMinutes : '0'.concat(timerMinutes))}`;
    newTimeString += `:${(seconds >= 10 ? seconds : '0'.concat(seconds))}`;
    /* eslint-enable max-len */
    return newTimeString;
  };

  const getTimeStringDaysLeft = () => {

    let newTimeString = '';

    newTimeString += `${timeLeft.days}${(timeLeft.days === 1 ? ' day' : ' days')}`;
    return newTimeString;
  };

  const getTimeStringAndMessaging = () => {

    if (isSBOTD && shouldShowSecondsRemaining && timeLeft) {
      return { timeString: timeLeft };
    }

    if (!shouldUseClockDisplay && !shouldUseDateDisplay) {
      return { messagingString: customMessagingString || 'Ends in ', timeString: getTimeStringDaysHoursMinutes() };
    }

    if (!(timeLeft && timeLeft.totalMilliseconds)) {
      return { messagingString: null, timeString: null };
    }

    if (shouldUseClockDisplay && (timeLeft.totalMilliseconds <= timebombThreshold)) {
      return { messagingString: customMessagingString || 'Ends in ', timeString: getTimeStringDigitalClock() };
    }

    if (shouldUseDaysLeftDisplay && (timeLeft.totalMilliseconds < daysLeftThreshold)) {
      return { messagingString: customMessagingString || 'Ends in ', timeString: getTimeStringDaysLeft() };
    }

    if (shouldUseDateDisplay && (timeLeft.totalMilliseconds < dateDisplayThreshold)) {
      return { messagingString: customMessagingString || 'Ends on ', timeString: getTimeStringDate() };
    }

    return { messagingString: null, timeString: null };
  };

  const [timeStringAndMessaging, setTimeStringAndMessaging] = useState(getTimeStringAndMessaging());

  useEffect(() => {

    const timeout = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
      setTimeStringAndMessaging((getTimeStringAndMessaging()));
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [timeLeft]);

  if (!(timerEndDate && timeLeft && timeStringAndMessaging && timeStringAndMessaging.timeString)) {
    return null;
  }

  const timerClasses = classNames('countdown-timer__wrapper', {
    'countdown-timer__wrapper--verbose': shouldUseMessagingString
  });

  return shouldUseBlock ? (
    <TimerBlock
      timeStringAndMessaging={timeStringAndMessaging}
      shouldIncludeSvg={shouldIncludeSvg}
      shouldUseMessagingString={shouldUseMessagingString}
    />
  ) : (
    <TimerInline
      timeStringAndMessaging={timeStringAndMessaging}
      shouldIncludeSvg={shouldIncludeSvg}
      shouldUseMessagingString={shouldUseMessagingString}
    />
  );
};

CountdownTimer.propTypes = {
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  timebombThreshold: PropTypes.number,
  daysLeftThreshold: PropTypes.number,
  dateDisplayThreshold: PropTypes.number,
  hours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minutes: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shouldUseMessagingString: PropTypes.bool,
  shouldShowSecondsRemaining: PropTypes.bool,
  shouldIncludeSvg: PropTypes.bool,
  shouldUseBlock: PropTypes.bool,
  shouldUseClockDisplay: PropTypes.bool,
  shouldUseDateDisplay: PropTypes.bool,
  shouldUseDaysLeftDisplay: PropTypes.bool,
  customMessagingString: PropTypes.string,
  isSBOTD: PropTypes.bool
};

CountdownTimer.defaultProps = {
  endDate: null,
  hours: 0,
  minutes: 0,
  timebombThreshold: 0,
  daysLeftThreshold: 0,
  dateDisplayThreshold: 0,
  shouldUseMessagingString: false,
  shouldShowSecondsRemaining: false,
  shouldIncludeSvg: false,
  shouldUseBlock: false,
  shouldUseClockDisplay: false,
  shouldUseDateDisplay: false,
  shouldUseDaysLeftDisplay: false,
  customMessagingString: null,
  isSBOTD: false
};

export default CountdownTimer;
